import React, { useEffect, useMemo, useState } from "react";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { CSVIcon, DeleteIcon, DisableDeleteIcon, EditIcon, EnableDeleteIcon, ListOptionsIcon } from "../assets/Icons";
import AddCheckpoint from "./modals/AddCheckpoint";
import QuestionSelectionListModal from "./modals/QuestionSelectionListModal";
import DeleteItem from "./modals/DeleteItem";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import axios from "axios";
import { useParams } from "react-router-dom";
import QuestionCSV from "./modals/QuestionCSV";
import Modal from "react-bootstrap/Modal";

const CheckListV2 = ({ setQestionLength, setQuestionMapping, setTabActions, onQuestionsLoaded }) => {
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const [addCheckpoint, setAddCheckpoint] = useState(false);
  const [deleteCheckpoint, setDeleteCheckpoint] = useState(false);
  const [selected, setSelected] = useState([]);
  const [deleteSingle, setDeleteSingle] = useState(false);
  const [editQuestion, seEditQuestion] = useState({});
  const [questions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { uuid } = useParams();
  const [CSVModal, setCSVModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const [inactiveQuestionId, setInactiveQuestionId] = useState(null);
  const [dragEnabled, setDragEnabled] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [savedOrder, setSavedOrder] = useState([]);
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);
  const [selectionQuestion, setSelectionQuestion] = useState(null);
  const [selectionQuestionIndex, setSelectionQuestionIndex] = useState(0);

  const fetchQuestions = async () => {
    try {
      let { data } = await axios.get(`${configData.config.API_URL}/api/machine/questions/all/${uuid}`, {
        headers: {
          Authorization: `Bearer ${userData.token}`,
          "Content-Type": "application/json",
        },
      });
      setAllQuestions(data.data);
      const mapping = {};
      data.data?.forEach((item) => {
        mapping[item.uuid] = item.title;
      });
      setQuestionMapping(mapping);
      setQestionLength(data.data.length);
      onQuestionsLoaded?.(data.data || []);
      setSavedOrder((data.data || []).map((q) => q.uuid));
      setOrderDirty(false);
      setLoading(false);
    } catch (error) {
      console.log("error", error);
    }
    setCSVModal(false);
    setProcessing(false);
  };

  const confirmInactiveQuestion = (id) => {
    setInactiveQuestionId(id);
    setShowInactiveModal(true);
  };

  const handleInactiveQuestion = async () => {
    try {
      await axios.put(
        `${configData.config.API_URL}/api/machine/questions/inActive/${inactiveQuestionId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        },
      );
      fetchQuestions();
      setShowInactiveModal(false);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleSelect = (id, checked) => {
    setSelected(checked ? [...selected, id] : selected.filter((e) => e !== id));
  };

  const handleSelectAll = () => {
    setSelected(selected.length === questions.length ? [] : questions.map((e) => e.uuid));
  };

  const handleEditQuestion = (elem, index) => {
    setAddCheckpoint(true);
    seEditQuestion({ active: true, data: elem, index });
  };

  const handleOpenSelectionModal = (elem, index) => {
    setSelectionQuestion(elem);
    setSelectionQuestionIndex(index);
    setSelectionModalOpen(true);
  };

  const isSelectionListEnabled = (question) =>
    Boolean(question?.selectionConfig?.enabled && question?.selectionConfig?.options?.length);

  useEffect(() => {
    setSelected(selected.length === questions.length ? questions.map((e) => e.uuid) : selected);
  }, [questions.length]);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const canDrag = dragEnabled && !savingOrder;

  const savedOrderIndex = useMemo(() => {
    const map = new Map();
    savedOrder.forEach((id, idx) => map.set(id, idx));
    return map;
  }, [savedOrder]);

  const revertToSavedOrder = () => {
    setAllQuestions((prev) => {
      if (!savedOrder || savedOrder.length === 0) return prev;
      const byId = new Map(prev.map((q) => [q.uuid, q]));
      const next = [];
      savedOrder.forEach((id) => {
        const q = byId.get(id);
        if (q) next.push(q);
      });
    
      prev.forEach((q) => {
        if (!savedOrderIndex.has(q.uuid)) next.push(q);
      });
      return next;
    });
    setOrderDirty(false);
    setDraggingId(null);
    setDragOverId(null);
  };

  const onToggleReorder = () => {
    setDragEnabled((prev) => {
      const next = !prev;
      if (prev === true && next === false) {
        if (orderDirty) revertToSavedOrder();
        setDraggingId(null);
        setDragOverId(null);
      }
      return next;
    });
  };

  const saveOrder = async () => {
    try {
      setSavingOrder(true);
      const orderedUuids = questions.map((q) => q.uuid);

      // NOTE: This is a single bulk call. Backend should persist order using this list.
      await axios.put(
        `${configData.config.API_URL}/api/machine/questions/reorder/${uuid}`,
        { orderedUuids },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setSavedOrder(orderedUuids);
      setOrderDirty(false);
      setDragEnabled(false);
    } catch (error) {
      console.log("error", error);
    } finally {
      setSavingOrder(false);
      setDraggingId(null);
      setDragOverId(null);
    }
  };

  const onDragStartRow = (id) => (e) => {
    if (!canDrag) return;
    setDraggingId(id);
    setDragOverId(null);
    // helps some browsers render a better drag preview
    try {
      e.dataTransfer.effectAllowed = "move";
    } catch (err) {
      // ignore
    }
  };

  const onDragOverRow = (id) => (e) => {
    if (!canDrag) return;
    e.preventDefault();
    if (dragOverId !== id) setDragOverId(id);
  };

  const onDropRow = (targetId) => (e) => {
    if (!canDrag) return;
    e.preventDefault();
    if (!draggingId || draggingId === targetId) {
      setDraggingId(null);
      setDragOverId(null);
      return;
    }

    setAllQuestions((prev) => {
      const fromIndex = prev.findIndex((q) => q.uuid === draggingId);
      const toIndex = prev.findIndex((q) => q.uuid === targetId);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return prev;

      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setOrderDirty(true);

    setDraggingId(null);
    setDragOverId(null);
  };

  const onDragEndRow = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  const canRearrangeQuestions = questions.length >= 2;
  const rearrangeButtonTitle = canRearrangeQuestions
    ? "Enable rearranging questions"
    : "Add more questions to rearrange";

  
  const saveOrderData = async () => {
    try {
      setSavingOrder(true);
      const questionsPayload = questions.map((q, index) => ({
        uuid: q.uuid,
        priority: index + 1,
      }));

      await axios.post(
        `${configData.config.API_URL}/api/machine/questions/update/questions-priority`,
        { questions: questionsPayload },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setSavedOrder(questions.map((q) => q.uuid));
      setOrderDirty(false);
      setDragEnabled(false);
    } catch (error) {
      console.log("error", error);
    } finally {
      setSavingOrder(false);
      setDraggingId(null);
      setDragOverId(null);
    }
  };
  useEffect(() => {
    if (!setTabActions) return undefined;
    if (loading || !questions.length || !userData?.user?.permissions?.includes("MACHINE.QUESTIONS.ADD")) {
      setTabActions(null);
      return undefined;
    }
    setTabActions(
      <div className="entity-info-v2__action-group">
        {!dragEnabled ? (
          <button
            type="button"
            className="entity-info-v2__btn-outline"
            onClick={onToggleReorder}
            disabled={savingOrder || loading || !canRearrangeQuestions}
            title={rearrangeButtonTitle}
          >
            <DragIndicatorOutlinedIcon />
            Rearrange Questions
          </button>
        ) : (
          <>
            <button type="button" className="entity-info-v2__btn-outline" onClick={onToggleReorder} disabled={savingOrder}>
              Cancel
            </button>
            <button
              type="button"
              className="entity-info-v2__btn-outline"
              onClick={saveOrderData}
              disabled={!orderDirty || savingOrder}
            >
              {savingOrder ? "Saving…" : "Save Order"}
            </button>
          </>
        )}
        <button type="button" className="entity-info-v2__btn-outline" onClick={() => setCSVModal(true)}>
          <UploadFileOutlinedIcon />
          Upload CSV
        </button>
        <button type="button" className="entity-info-v2__btn-primary" onClick={() => setAddCheckpoint(true)}>
          + Add Question
        </button>
      </div>,
    );
    return () => setTabActions(null);
  }, [loading, questions.length, dragEnabled, orderDirty, savingOrder, canRearrangeQuestions, setTabActions, userData?.user?.permissions]);

  return (
    <>
      <div className="entity-info-v2__table-area">
        {loading ? (
            <table className="entity-info-v2__table">
                  <thead>
                    <tr>
                      <th className="text-center">S.No</th>
                      <th>Questions</th>
                      <th className="text-center">Tags</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="text-center">
                        <Skeleton width="3vw" height="1.65rem" />
                      </td>
                      <td>
                        {" "}
                        <Skeleton width="20vw" height="1.65rem" />{" "}
                      </td>
                      <td className="text-center">
                        {" "}
                        <Skeleton width="5vw" height="1.65rem" />{" "}
                      </td>
                      <td className="text-center">
                        {" "}
                        <Skeleton width="5vw" height="1.65rem" />{" "}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-center">
                        <Skeleton width="3vw" height="1.65rem" />
                      </td>
                      <td>
                        {" "}
                        <Skeleton width="20vw" height="1.65rem" />{" "}
                      </td>
                      <td className="text-center">
                        {" "}
                        <Skeleton width="5vw" height="1.65rem" />{" "}
                      </td>
                      <td className="text-center">
                        {" "}
                        <Skeleton width="5vw" height="1.65rem" />{" "}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-center">
                        <Skeleton width="3vw" height="1.65rem" />
                      </td>
                      <td>
                        {" "}
                        <Skeleton width="20vw" height="1.65rem" />{" "}
                      </td>
                      <td className="text-center">
                        {" "}
                        <Skeleton width="5vw" height="1.65rem" />{" "}
                      </td>
                      <td className="text-center">
                        {" "}
                        <Skeleton width="5vw" height="1.65rem" />{" "}
                      </td>
                    </tr>
                  </tbody>
                </table>
        ) : (
          <>
            {questions.length > 0 ? (
              <table className="entity-info-v2__table">
                    <thead>
                      <tr>
                        <th className="text-center">S.No</th>
                        <th>Questions</th>
                        <th className="text-center">Tags</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {questions.length > 0 &&
                        questions.map((elem, index) => (
                          <tr
                            key={elem.uuid}
                            draggable={canDrag}
                            onDragStart={onDragStartRow(elem.uuid)}
                            onDragOver={onDragOverRow(elem.uuid)}
                            onDrop={onDropRow(elem.uuid)}
                            onDragEnd={onDragEndRow}
                            className={[
                              "checklist-row-draggable",
                              canDrag ? "is-enabled" : "is-disabled",
                              draggingId === elem.uuid ? "is-dragging" : "",
                              dragOverId === elem.uuid && draggingId && draggingId !== elem.uuid ? "is-drag-over" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            <td className="text-center">
                              <div className="d-flex align-items-center justify-content-center">
                                <div className="form-check listchild">
                                  {/* <input
                                  onChange={(e) => handleSelect(elem.uuid, e.target.checked)}
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={selected.includes(elem.uuid)}
                                  id={`check-${elem.uuid}`}
                                /> */}
                                  <label className="form-check-label" htmlFor={`check-${elem.uuid}`}>
                                    {dragEnabled && (
                                      <span
                                        className="checklist-drag-handle"
                                        title="Drag to reorder"
                                        aria-hidden="true"
                                      >
                                        ⋮⋮
                                      </span>
                                    )}
                                    {index + 1}
                                  </label>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="entity-info-v2__question-cell">
                                <span>{elem.title}</span>
                                {isSelectionListEnabled(elem) && (
                                  <span className="entity-info-v2__question-badge" title="Mobile popup list configured">
                                    List enabled · {elem.selectionConfig.options.length}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="text-center">
                              <div className="entity-info-v2__tags-inline">
                                  {elem.tags.length
                                    ? elem.tags.map((e) => (
                                        <span key={e.id} className="entity-info-v2__tag">
                                          {e.name}
                                        </span>
                                      ))
                                    : <span className="entity-info-v2__muted">—</span>}
                              </div>
                            </td>
                            <td className="text-center">
                              <div className="entity-info-v2__row-actions">
                                {userData?.user?.permissions?.includes("MACHINE.QUESTIONS.ADD") && (
                                  <button type="button" className="entity-info-v2__icon-btn" onClick={() => handleEditQuestion(elem, index)} title="Edit">
                                    <EditIcon />
                                  </button>
                                )}
                                {userData?.user?.permissions?.includes("MACHINE.QUESTIONS.ADD") && (
                                  <button
                                    type="button"
                                    className={`entity-info-v2__icon-btn ${isSelectionListEnabled(elem) ? "is-active" : ""}`}
                                    title="Configure mobile popup list"
                                    onClick={() => handleOpenSelectionModal(elem, index)}
                                  >
                                    <ListOptionsIcon active={isSelectionListEnabled(elem)} />
                                  </button>
                                )}
                                <button type="button" className="entity-info-v2__icon-btn entity-info-v2__icon-btn--danger" onClick={() => confirmInactiveQuestion(elem.uuid)} title="Delete">
                                  <DeleteIcon />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
            ) : (
              <>
                {userData?.user?.permissions?.includes("MACHINE.QUESTIONS.ADD") ? (
                  <>
                    <div className="row">
                      <div className="px-4">
                        <div className="first-event">
                          No Question added yet!
                          <div className="d-flex gap-3">
                            <button className="plain-btn icon" onClick={() => setCSVModal(true)}>
                              <CSVIcon />
                              Upload CSV
                            </button>
                            <button className="plain-btn" onClick={() => setAddCheckpoint(true)}>
                              Add Question
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </>
            )}
          </>
        )}
      </div>

      <AddCheckpoint
        addCheckpoint={addCheckpoint}
        setAddCheckpoint={setAddCheckpoint}
        fetchQuestions={fetchQuestions}
        size={questions.length}
        editQuestion={editQuestion}
        seEditQuestion={seEditQuestion}
      />

      <QuestionSelectionListModal
        open={selectionModalOpen}
        onClose={() => {
          setSelectionModalOpen(false);
          setSelectionQuestion(null);
        }}
        question={selectionQuestion}
        questionIndex={selectionQuestionIndex}
        onSaved={fetchQuestions}
      />

      <DeleteItem
        setSelected={setSelected}
        selected={selected}
        deleteCheckpoint={deleteCheckpoint}
        fetchQuestions={fetchQuestions}
        setDeleteCheckpoint={setDeleteCheckpoint}
        deleteSingle={deleteSingle}
      />

      <QuestionCSV
        fetchQuestions={fetchQuestions}
        machineId={uuid}
        CSVModal={CSVModal}
        setCSVModal={setCSVModal}
        setProcessing={setProcessing}
        processing={processing}
      />

      <Modal centered show={showInactiveModal} onHide={() => setShowInactiveModal(false)}>
        <Modal.Header className="popupheader delete pt-4">Delete Question</Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-12">
              <div className="form-group pt-4 pb-5 m-0">
                <p className="popupheading delete p-0 m-0">Do you really want to delete this question</p>
              </div>
              <div className="row">
                <div className="col-12 d-flex justify-content-center mt-2 mb-2 footerbtn">
                  <button className="cancel" onClick={() => setShowInactiveModal(false)}>
                    Cancel
                  </button>
                  <button className="save" onClick={handleInactiveQuestion}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CheckListV2;
